import { useContext, useEffect, useRef, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GlobalContext } from "../Context/GlobalContext";
import storage from "../Assets/js/Global";
import { imagePath } from "../Url";
import LogOutModal from "../modals/LogOutModal";
import {
  House,
  LogOut,
  AlignLeft,
  ShieldAlert,
  History,
  Files,
  ReceiptText,
  User,
  Info,
  Newspaper,
  Share2,
  Bell,
  Dot,
  ScanFace,
  Bot,
} from "lucide-react";
import { images } from "../Assets/images";
import { convertUtcToLocal } from "../utils";

const Header = () => {
  const {
    doLogout,
    setIsVerified,
    translatedLabels,
    logoUrl,
    headerLogoUrl,
    setShowExportModal,
    isNotificationReceived,
    handleNotificationClick,
    setIsNotificationReceived,
    getGuestUserList,
    is4Life,
  } = useContext(GlobalContext);
  const isPlanActive = storage.get("isPlanActive");
  const screenLabels = translatedLabels?.common;
  const authLabels = translatedLabels?.auth;
  const user = storage.get("loginObj");
  const route = useLocation();
  const navigate = useNavigate();
  const currentPath = route.pathname;
  const location = useLocation();
  const selectedDate = location.state?.selectedDate;
  const previousPath = useRef(location.pathname);
  const campaign = location.state?.campaign || location?.state?.campaignData;

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [show, setShow] = useState(false);

  const menuIconRoutes = [
    "/dashboard",
    "/dashboard2",
    "/documents",
    "/articles",
    "/history",
    "/privacy-policy",
    "/terms-condition",
    "/profile",
    "/about-us",
    "/notification",
    "/guest-scan",
    "/campaign-list",
    "/survey-form",
    "/ask-ai",
    "/user-scan-details-four-life"
  ];
  const profilePaths = [
    "/profile",
    "/profile-update",
    "/change-password",
    "/deleteAcc-one",
    "/language-selection",
  ];

  const isDashboard = menuIconRoutes.includes(currentPath);
  const isProfileActive = profilePaths.includes(location.pathname);

  const formatDateTranslated = (timestamp, screenLabels) => {
    if (!timestamp) return "";

    const isNumeric = !isNaN(Number(timestamp));
    const timestampValue = isNumeric ? Number(timestamp) : timestamp;

    const localDate = convertUtcToLocal(timestampValue);
    if (isNaN(localDate)) return "";

    const weekdays = [
      screenLabels?.sun?.label_value || "Sun",
      screenLabels?.mon?.label_value || "Mon",
      screenLabels?.tue?.label_value || "Tue",
      screenLabels?.wed?.label_value || "Wed",
      screenLabels?.thu?.label_value || "Thu",
      screenLabels?.fri?.label_value || "Fri",
      screenLabels?.sat?.label_value || "Sat",
    ];

    const months = [
      screenLabels?.jan?.label_value || "Jan",
      screenLabels?.feb?.label_value || "Feb",
      screenLabels?.march?.label_value || "Mar",
      screenLabels?.april?.label_value || "Apr",
      screenLabels?.may?.label_value || "May",
      screenLabels?.june?.label_value || "Jun",
      screenLabels?.july?.label_value || "Jul",
      screenLabels?.aug?.label_value || "Aug",
      screenLabels?.sep?.label_value || "Sep",
      screenLabels?.oct?.label_value || "Oct",
      screenLabels?.nov?.label_value || "Nov",
      screenLabels?.dec?.label_value || "Dec",
    ];

    const weekday = weekdays[localDate.getDay()];
    const month = months[localDate.getMonth()];
    const day = localDate.getDate();
    const year = localDate.getFullYear();

    const hours = localDate.getHours();
    const minutes = localDate.getMinutes().toString().padStart(2, "0");
    const amPm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;

    const timePart = `${hour12}:${minutes} ${amPm}`;

    return `${weekday}, ${month} ${day}, ${year} | ${timePart}`;
  };

  const routeTitles = {
    "/dashboard": (
      <img
        src={headerLogoUrl || logoUrl || images.appLogo}
        alt="app-logo"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = images.appLogo;
        }}
        style={{ height: "28px" }}
      />
    ),
    "/dashboard2": screenLabels?.dashboard?.label_value || "Dashboard",
    "/privacy-policy":
      screenLabels?.privacy_policy?.label_value || "Privacy Policy",
    "/terms-condition":
      screenLabels?.terms_conditions?.label_value || "Terms & Conditions",
    "/profile": screenLabels?.profile?.label_value || "Profile",
    "/profile-update":
      screenLabels?.update_profile?.label_value || "Update Profile",
    "/language-selection":
      authLabels?.select_language?.label_value || "Select Language",
    "/deleteAcc-one":
      screenLabels?.delete_account?.label_value || "Delete Account",
    "/help-guide": screenLabels?.help_and_guide?.label_value || "Help & Guide",
    "/change-password":
      screenLabels?.change_pass?.label_value || "Change Password",
    "/start-scan": screenLabels?.start_scan?.label_value || "Scan",
    "/history": screenLabels?.history?.label_value || "History",
    "/documents": screenLabels?.documents?.label_value || "Documents",
    "/view-more-details":
      screenLabels?.all_scan_details?.label_value || "All Scan Details",
    "/show-all-result":
      screenLabels?.all_scan_details?.label_value || "All Scan Details",
    "/show-all-result-2":
      screenLabels?.all_scan_details?.label_value || "All Scan Details",
    "/upload-document": storage.get("documentUpdate")
      ? screenLabels?.update_docs?.label_value || "Update Document"
      : screenLabels?.upload_docs?.label_value || "Upload Document",
    "/articles": screenLabels?.resources?.label_value || "Resources",
    "/user-scan-details": selectedDate
      ? formatDateTranslated(selectedDate, screenLabels)
      : screenLabels?.scan_details?.label_value || "Scan Details",
    "/user-scan-details-four-life": screenLabels?.history?.label_value || "History",
    "/about-us": screenLabels?.about_us?.label_value || "About Us",
    "/privacy-consent":
      screenLabels?.export_report?.label_value || "Export Report",
    "/export-document":
      screenLabels?.export_document?.label_value || "Export Document",
    "/view-export": screenLabels?.export_list?.label_value || "Export List",
    "/notification": screenLabels?.notification?.label_value || "Notification",
    "/guest-scan": screenLabels?.guest_scan?.label_value || "Guest Scan",
    "/guest-history":
      screenLabels?.guest_history?.label_value || "Guest History",
    "/guest-list": screenLabels?.guest_list?.label_value || "Guest List",
    "/survey-form": screenLabels?.survey_form?.label_value || "Survey Form",
    "/product-details":
      screenLabels?.recommended_product?.label_value || "Product Details",
    "/recommended-product":
      screenLabels?.recommended_product?.label_value || "Recommended Product",
    "/campaign-list": screenLabels?.campaigns?.label_value || "Campaigns",
    "/campaign-create": campaign
      ? "Update Campaign"
      : screenLabels?.create_campaign?.label_value || "Create Campaign",
    "/campaign-details": campaign?.name,
    "/product-variants":
      screenLabels?.product_varient?.label_value || "Product Varients",
    "/view-questions": screenLabels?.view_questions?.label_value || "Questions",
    "/ask-ai":
      screenLabels?.your_personal_health_assistant?.label_value ||
      "Your personal health assistant",
    "/plan-expired":
      screenLabels?.plan_expired_header?.label_value || "Plan Expired",
  };

  let pageTitle = routeTitles[route.pathname] || "App";

  if (currentPath.startsWith("/articles/")) {
    pageTitle = screenLabels?.details?.label_value || "Details";
  }

  const handleBack = () => {
    const currentPath = location.pathname;

    if (
      currentPath === "/deleteAcc-one" ||
      currentPath === "/change-password"
    ) {
      setIsVerified(false);
      navigate("/profile");
    } else if (currentPath === "/profile") {
      navigate(is4Life ? "/dashboard2" : "/dashboard");
    } else if (currentPath === "/show-all-result" && location.state?.scanId) {
      navigate("/user-scan-details-four-life", {
        state: {
          restoreSelectedScan: true,
          selectedScanKey: location.state.scanId,
        },
      });
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const excludedRoutes = ["/change-password"];
    const wasExcluded = excludedRoutes.includes(previousPath.current);
    const isNowExcluded = excludedRoutes.includes(location.pathname);
    if (wasExcluded && !isNowExcluded) {
      storage.set("UserModule", "Login");
    }
    previousPath.current = location.pathname;

    if (location.pathname === "/notification") {
      setIsNotificationReceived(false);
      localStorage.setItem("notificationsSeen", "true");
    }
  }, [location.pathname]);

  const homeRoute = is4Life ? "/dashboard2" : "/dashboard";

  const userName = `${user?.firstName} ${user?.lastName}`;

  return (
    <>
      <nav className="navbar fixed-top header-part">
        <div className="nav-link navbar-brand text-black d-flex gap-2 align-items-center w-100 p-0 justify-content-between me-0">
          {isDashboard ? (
            <span onClick={() => setShow(true)}>
              <AlignLeft className="cursor-pointer" />
            </span>
          ) : (
            <Link
              onClick={(e) => {
                e.preventDefault();
                handleBack();
              }}
              className="nav-link navigation-icon me-2"
            >
              <i className="fa-solid fa-angle-left"></i>
            </Link>
          )}
          <h5 className="header-text mb-0 text-center">{pageTitle}</h5>
          <div className="" onClick={() => setShowExportModal(true)}>
            {(currentPath === "/user-scan-details" || currentPath === "/user-scan-details-four-life") && <Share2 />}
            {(currentPath === "/dashboard" ||
              currentPath === "/dashboard2") && (
                <Link
                  to="/notification"
                  className="nav-link"
                  onClick={handleNotificationClick}
                >
                  {isNotificationReceived && (
                    <div className="notification-count">
                      <Dot size={40} color="red" />
                    </div>
                  )}
                  <Bell className="position-relative" />
                </Link>
              )}
          </div>
        </div>
      </nav>

      <Offcanvas
        show={show}
        onHide={() => setShow(false)}
        placement="start"
        className="p-0 text-black shadow-lg sidebar-background"
        style={{ width: "240px" }}
      >
        <Offcanvas.Body className="p-0">
          <div
            id="sideBarUserDetails"
            className="sideBarUserDetails shadow-bottom"
          >
            <div
              className="d-flex align-items-center gap-2"
              data-bs-toggle="collapse"
              data-bs-target="#emailDropdown"
            >
              <div
                className="d-flex gap-1 align-items-center"
                onClick={() => {
                  navigate("/profile");
                  setShow(false);
                }}
              >
                <div className="menu-profile-image">
                  <img
                    src={imagePath + user?.profilePhoto || images.userImage}
                    alt="user-img"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = images.userImage;
                    }}
                  />
                </div>
              </div>
              <div className="w-100">
                <h6 className="user-name-text mb-0">
                  {userName}
                </h6>
                <div className="menu-profile-info">
                  <p className="user-email">
                    {is4Life ? user?.userSecondaryEmail : user?.userEmail}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="sidebar-list">
            <div>
              <Link
                to={homeRoute}
                className={`nav-link option-list ${location.pathname === homeRoute ? "active-option" : ""
                  }`}
                onClick={() => setShow(false)}
              >
                <div
                  className={`sidebar-icon ${location.pathname === homeRoute ? "active-option-icon" : ""
                    }`}
                >
                  <House size={20} />
                </div>
                {screenLabels?.home?.label_value || "Home"}
              </Link>

              {!is4Life && (
                <>
                  <Link
                    to={"/articles"}
                    className={`nav-link option-list ${location.pathname === "/articles" ? "active-option" : ""
                      }`}
                    onClick={() => setShow(false)}
                  >
                    <div
                      className={`sidebar-icon ${location.pathname === "/articles"
                        ? "active-option-icon"
                        : ""
                        }`}
                    >
                      <Newspaper size={20} />
                    </div>
                    {screenLabels?.resources?.label_value || "Resources"}
                  </Link>

                  <Link
                    to={"/documents"}
                    className={`nav-link option-list ${location.pathname === "/documents" ? "active-option" : ""
                      }`}
                    onClick={() => setShow(false)}
                  >
                    <div
                      className={`sidebar-icon ${location.pathname === "/documents"
                        ? "active-option-icon"
                        : ""
                        }`}
                    >
                      <Files className="" size={20} />
                    </div>
                    {screenLabels?.documents?.label_value || "Documents"}
                  </Link>
                </>
              )}
              {user?.role === 3 && (
                <>
                  <div
                    className={`nav-link option-list cursor-pointer ${location.pathname === "/guest-scan" ||
                      location.pathname === "/guest-list"
                      ? "active-option"
                      : ""
                      }`}
                    onClick={() => {
                      setShow(false);
                      getGuestUserList();
                    }}
                  >
                    <div
                      className={`sidebar-icon ${location.pathname === "/guest-scan" ||
                        location.pathname === "/guest-list"
                        ? "active-option-icon"
                        : ""
                        }`}
                    >
                      <ScanFace className="" size={20} />
                    </div>
                    {screenLabels?.guest_scan?.label_value || "Guest Scan"}
                  </div>

                  {/* <Link
                    to="/campaign-list"
                    className={`nav-link option-list cursor-pointer ${
                      location.pathname === "/campaign-list" ||
                      location.pathname === "/campaign-create"
                        ? "active-option"
                        : ""
                    }`}
                    onClick={() => {
                      setShow(false);
                    }}
                  >
                    <div
                      className={`sidebar-icon ${
                        location.pathname === "/campaign-list" ||
                        location.pathname === "/campaign-create"
                          ? "active-option-icon"
                          : ""
                      }`}
                    >
                      <ScanQrCode className="" size={20} />
                    </div>
                    {screenLabels?.campaigns?.label_value || "Campaigns"}
                  </Link> */}
                </>
              )}

              {/* <Link
                to="/survey-form"
                className={`nav-link option-list cursor-pointer ${location.pathname === "/survey-form" ? "active-option" : ""
                  }`}
                onClick={() => {
                  setShow(false);
                }}
              >
                <div
                  className={`sidebar-icon ${location.pathname === "/survey-form"
                      ? "active-option-icon"
                      : ""
                    }`}
                >
                  <ClipboardList size={20} />
                </div>
                {screenLabels?.survey_form?.label_value || "Survey Form"}
              </Link> */}

              <Link
                to={"/notification"}
                className={`nav-link option-list ${location.pathname === "/notification" ? "active-option" : ""
                  }`}
                onClick={() => setShow(false)}
              >
                <div
                  className={`sidebar-icon ${location.pathname === "/notification"
                    ? "active-option-icon"
                    : ""
                    }`}
                >
                  <Bell size={20} />
                </div>
                {screenLabels?.notification?.label_value || "Notification"}
              </Link>
              {!is4Life && (
                <Link
                  to={isPlanActive ? "/ask-ai" : "/plan-expired"}
                  className={`nav-link option-list ${location.pathname === "/ask-ai" ? "active-option" : ""
                    }`}
                  onClick={() => setShow(false)}
                >
                  <div
                    className={`sidebar-icon ${location.pathname === "/ask-ai" ? "active-option-icon" : ""
                      }`}
                  >
                    <Bot size={20} />
                  </div>
                  {screenLabels?.ask_ai?.label_value || "Ask AI"}
                </Link>
              )}
              <Link
                to={is4Life ? "/user-scan-details-four-life" : "/history"}
                className={`nav-link option-list ${["/history", "/user-scan-details-four-life"].includes(location.pathname) ? "active-option" : ""
                  }`}
                onClick={() => setShow(false)}
              >
                <div
                  className={`sidebar-icon ${["/history", "/user-scan-details-four-life"].includes(location.pathname) ? "active-option-icon" : ""
                    }`}
                >
                  <History className="" size={20} />
                </div>
                {screenLabels?.history?.label_value || "History"}
              </Link>
              <Link
                to={"/profile"}
                className={`nav-link option-list ${isProfileActive ? "active-option" : ""
                  }`}
                onClick={() => setShow(false)}
              >
                <div
                  className={`sidebar-icon ${location.pathname === "/profile" ? "active-option-icon" : ""
                    }`}
                >
                  <User className="" size={20} />
                </div>
                {screenLabels?.profile?.label_value || "Profile"}
              </Link>
              <Link
                to={"/about-us"}
                className={`nav-link option-list ${location.pathname === "/about-us" ? "active-option" : ""
                  }`}
                onClick={() => setShow(false)}
              >
                <div
                  className={`sidebar-icon ${location.pathname === "/about-us"
                    ? "active-option-icon"
                    : ""
                    }`}
                >
                  <Info size={20} />
                </div>
                {screenLabels?.about_us?.label_value || "About Us"}
              </Link>

              <Link
                to={"/privacy-policy"}
                className={`nav-link option-list ${location.pathname === "/privacy-policy" ? "active-option" : ""
                  }`}
                onClick={() => setShow(false)}
              >
                <div
                  className={`sidebar-icon ${location.pathname === "/privacy-policy"
                    ? "active-option-icon"
                    : ""
                    }`}
                >
                  <ShieldAlert className="" size={20} />
                </div>
                {screenLabels?.privacy_policy?.label_value || "Privacy Policy"}
              </Link>
              <Link
                to={"/terms-condition"}
                className={`nav-link option-list ${location.pathname === "/terms-condition"
                  ? "active-option"
                  : ""
                  }`}
                onClick={() => setShow(false)}
              >
                <div
                  className={`sidebar-icon ${location.pathname === "/terms-condition"
                    ? "active-option-icon"
                    : ""
                    }`}
                >
                  <ReceiptText size={20} />
                </div>
                {screenLabels?.terms_conditions?.label_value ||
                  "Terms & Conditions"}
              </Link>
            </div>
            <p className="text-center text-muted fs-14px mt-4">
              {screenLabels?.abt_us_2?.label_value || "App Version: 3.0"}
            </p>
            <div
              className="logout-link"
              onClick={() => setShowLogoutModal(true)}
            >
              <div className="sidebar-icon sidebar-btn-icon me-2">
                <LogOut size={20} strokeWidth="3" />
              </div>
              {screenLabels?.log_out?.label_value || "Log out"}
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
      <LogOutModal
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          setShowLogoutModal(false);
          doLogout();
        }}
        screenLabels={screenLabels}
      />
    </>
  );
};

export default Header;
